/**
 * Centralized card visibility and availability for listing cards.
 * See docs/CARD_VISIBILITY_ANALYSIS.md.
 */
import moment from 'moment';

/** Hours before start date after which registration is closed (card still shown, disabled). */
export const BOOKING_CUTOFF_HOURS = Number(
  typeof process !== 'undefined' && process.env && process.env.REACT_APP_BOOKING_CUTOFF_HOURS
) || 48;

/**
 * True if now is within cutoff window.
 * Uses item.cutoff_days_before_start when present (X days before start_date), else BOOKING_CUTOFF_HOURS.
 * Used for Group Trips and Shared Rentals only.
 */
export function isRegistrationClosedByCutoff(item) {
  if (!item || !item.start_date) return false;
  try {
    const startMoment = moment(item.start_date);
    if (!startMoment.isValid()) return false;
    const days = item.cutoff_days_before_start;
    const cutoffMoment = (days != null && Number.isInteger(days) && days >= 0)
      ? startMoment.clone().subtract(days, 'days')
      : startMoment.clone().subtract(BOOKING_CUTOFF_HOURS, 'hours');
    return moment().isAfter(cutoffMoment);
  } catch (e) {
    return false;
  }
}

/**
 * Single source of truth for card visibility and disabled state.
 * Use `reason` for branching in UI; use `label` only for display text.
 * @param {Object} item - group_tour / shared_rental / package
 * @param {'group_tour'|'shared_rental'|'private_tour'} module
 * @returns {{ visible: boolean, disabled: boolean, label: string|null, reason: 'cutoff'|'full'|null }}
 */
export function getCardVisibilityState(item, module) {
  if (!item) return { visible: false, disabled: true, label: null, reason: null };

  if (module === 'private_tour') {
    if (item.is_deleted === true || item.published === false) {
      return { visible: false, disabled: true, label: null, reason: null };
    }
    return { visible: true, disabled: false, label: null, reason: null };
  }

  // Group Trips & Shared Rentals
  if (item.is_deleted === true || item.published !== true) {
    return { visible: false, disabled: true, label: null, reason: null };
  }

  const registrationClosedByCutoff = isRegistrationClosedByCutoff(item);
  const slotFull = item.spots_left != null && item.spots_left <= 0;
  const accepting = item.accepting_registrations !== false;

  const disabled = registrationClosedByCutoff || !accepting || slotFull;
  let label = null;
  let reason = null;
  if (registrationClosedByCutoff) {
    const days = item.cutoff_days_before_start;
    label = (days != null && Number.isInteger(days) && days >= 0)
      ? `Registration closed ${days} days before start for operational preparation.`
      : 'Registration closed';
    reason = 'cutoff';
  } else if (slotFull || !accepting) {
    label = module === 'group_tour' ? 'Full' : 'Rental full';
    reason = 'full';
  }

  return { visible: true, disabled, label, reason };
}

/**
 * Sort order for event cards: partially filled first, then empty, then full last.
 * Used by Group Trips and Shared Rentals list pages only.
 */
export function slotSortOrder(item) {
  const full = (item.spots_left != null && item.spots_left <= 0) || item.accepting_registrations === false;
  const people = item.paid_people_count ?? item.paid_interests_count ?? 0;
  if (full) return 2;
  if (people === 0) return 1;
  return 0;
}

/**
 * Filter raw list to only items that should be shown (visible).
 * @param {Array} raw - array of group_tour / shared_rental / package
 * @param {'group_tour'|'shared_rental'|'private_tour'} module
 */
export function filterVisibleItems(raw, module) {
  if (!Array.isArray(raw)) return [];
  return raw.filter((t) => getCardVisibilityState(t, module).visible);
}
